// ...existing code...
import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import {
  FaPlus,
  FaReceipt,
  FaList,
  FaCheck,
  FaTimes,
  FaSave,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const getAuthToken = () => localStorage.getItem('authToken');

const Receipt = () => {
  const { selectedAcademicYear } = useAppContext();
  // Focus 'Admission No.' input on mount
  useEffect(() => {
    if (admissionInputRef.current) admissionInputRef.current.focus();
  }, []);
  const navigate = useNavigate();
  const [newReceipt, setNewReceipt] = useState({
    from_account: '',
    to_account: '',
    from_account_id: '',
    to_account_id: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    admission_no: '', // new field
    academic_year_id: '', // will be set from context/left bar
    installment_ids: [], // array of selected installment ids
    installment_amounts: {}, // {id: amount}
  });
  const [accounts, setAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [studentInstallments, setStudentInstallments] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [helperAcademicYear, setHelperAcademicYear] = useState(null);
  const [academicYearMismatch, setAcademicYearMismatch] = useState(false);
  const [feeSummary, setFeeSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromAccounts, setFilteredFromAccounts] = useState([]);
  const [filteredToAccounts, setFilteredToAccounts] = useState([]);
  const [toDropdownIndex, setToDropdownIndex] = useState(-1); // For keyboard navigation
  const [fromDropdownIndex, setFromDropdownIndex] = useState(-1); // For keyboard navigation
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const fromInputRef = useRef(null); // For focusing from account
  const admissionInputRef = useRef(null); // For focusing after save
  const dateInputRef = useRef(null); // For F2 shortcut

  // Handle Ctrl+S for save and F2 for date focus (only on this page)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        addReceipt();
      }
      if (e.key === 'F2') {
        e.preventDefault();
        if (dateInputRef.current) dateInputRef.current.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [newReceipt]);

  useEffect(() => {
    fetchAccounts();
    fetchCashAccounts();
    // fetchAcademicYears(); // Academic year will be set from context/left bar
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target)
      ) {
        setShowFromDropdown(false);
      }
      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target)
      ) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-accounts?all=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAccounts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAlert({
        show: true,
        message: 'Failed to load accounts',
        variant: 'danger',
      });
    }
  };

  const fetchCashAccounts = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-accounts`,
        { account_type_ids: [9, 10] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.data) {
        setCashAccounts(response.data.data);
      } else {
        console.error('Failed to fetch cash accounts', response.data);
        setAlert({
          show: true,
          message: 'Failed to fetch cash accounts',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAlert({
        show: true,
        message: 'Error loading cash accounts',
        variant: 'danger',
      });
    }
  };

  const fetchAcademicYears = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-academic-years`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.data) {
        setAcademicYears(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching academic years:', error);
    }
  };

  const fetchStudentInstallments = async (studentId, academicYearId) => {
    const token = getAuthToken();
    if (!token || !studentId || !academicYearId) return;

    setLoadingInstallments(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_student_due_installments`,
        {
          student_id: studentId,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.status === 'success') {
        setStudentInstallments(response.data.installments || []);
        setSelectedStudent(response.data.student);
      } else {
        setStudentInstallments([]);
        setSelectedStudent(null);
        setAlert({
          show: true,
          message:
            response.data.message || 'No installments found for this student',
          variant: 'warning',
        });
      }
    } catch (error) {
      console.error('Error fetching student installments:', error);
      setStudentInstallments([]);
      setSelectedStudent(null);
      setAlert({
        show: true,
        message: 'Error loading student installments',
        variant: 'danger',
      });
    } finally {
      setLoadingInstallments(false);
    }
  };

  // Effect to fetch student fee receipt helper when admission_no is entered
  // Remove effect for admission_no, will trigger manually

  // Set academic_year_id from context/left bar
  useEffect(() => {
    if (selectedAcademicYear && selectedAcademicYear.id) {
      setNewReceipt((prev) => ({
        ...prev,
        academic_year_id: selectedAcademicYear.id,
      }));
    }
  }, [selectedAcademicYear]);

  // Reload installments when academic year changes while an admission number is present
  useEffect(() => {
    if (
      selectedAcademicYear?.id &&
      newReceipt.admission_no &&
      newReceipt.admission_no.toString().trim() !== ''
    ) {
      fetchStudentFeeReceiptHelper(
        newReceipt.admission_no,
        selectedAcademicYear.id,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAcademicYear]);

  const fetchStudentFeeReceiptHelper = async (admissionNo, academicYearId) => {
    const token = getAuthToken();
    if (!token || !admissionNo || !academicYearId) return;
    setLoadingInstallments(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_student_fee_receipt_helper`,
        {
          student_admission_no: admissionNo,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.status === 'success') {
        const helperYear = response.data.academic_year || null;
        const mismatch =
          helperYear &&
          selectedAcademicYear?.id &&
          helperYear.id !== selectedAcademicYear.id;

        setHelperAcademicYear(helperYear);
        setAcademicYearMismatch(mismatch);
        setSelectedStudent(response.data.student);
        setFeeSummary(response.data.summary);
        setStudentInstallments(response.data.installments || []);

        // If mismatch, clear installment selections and amount
        if (mismatch) {
          setNewReceipt((prev) => ({
            ...prev,
            installment_ids: [],
            installment_amounts: {},
            amount: '',
            from_account: response.data.student.account_name || '',
            from_account_id: response.data.student.account_id || '',
          }));
          setAlert({
            show: true,
            message: `Academic year mismatch: Installments belong to ${helperYear.name} but selected academic year is ${selectedAcademicYear?.name || 'N/A'}. Please switch academic year or clear admission number.`,
            variant: 'warning',
          });
        } else {
          // Prefill Received From when year matches
          setNewReceipt((prev) => ({
            ...prev,
            from_account: response.data.student.account_name || '',
            from_account_id: response.data.student.account_id || '',
          }));
        }
      } else {
        setHelperAcademicYear(null);
        setAcademicYearMismatch(false);
        setStudentInstallments([]);
        setSelectedStudent(null);
        setFeeSummary(null);
        setAlert({
          show: true,
          message:
            response.data.message || 'No installments found for this student',
          variant: 'warning',
        });
      }
    } catch (error) {
      setHelperAcademicYear(null);
      setAcademicYearMismatch(false);
      setStudentInstallments([]);
      setSelectedStudent(null);
      setFeeSummary(null);
      setAlert({
        show: true,
        message: 'Error loading student installments',
        variant: 'danger',
      });
    } finally {
      setLoadingInstallments(false);
    }
  };

  const addReceipt = async () => {
    // Academic year mismatch check
    if (academicYearMismatch) {
      setAlert({
        show: true,
        message:
          'Cannot save receipt because student installments belong to a different academic year. Please switch academic year or clear admission number.',
        variant: 'danger',
      });
      return;
    }

    // Validation
    if (!newReceipt.from_account_id || !newReceipt.to_account_id) {
      setAlert({
        show: true,
        message: 'Please select both accounts',
        variant: 'warning',
      });
      return;
    }
    if (!newReceipt.amount || parseFloat(newReceipt.amount) <= 0) {
      setAlert({
        show: true,
        message: 'Please enter a valid amount',
        variant: 'warning',
      });
      return;
    }
    // If fee receipt, validate at least one installment selected
    if (newReceipt.admission_no && studentInstallments.length > 0) {
      if (!newReceipt.installment_ids.length) {
        setAlert({
          show: true,
          message: 'Please select at least one installment',
          variant: 'warning',
        });
        return;
      }
    }

    setLoading(true);
    setAlert({ show: false, message: '', variant: '' });
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      setLoading(false);
      return;
    }

    // Prepare payload for v2
    let payload = { ...newReceipt };
    // Remove unused fields for API
    delete payload.installment_ids;
    delete payload.installment_amounts;
    delete payload.student_id;
    delete payload.installment_id;

    // If fee receipt with installments, add fee_installments array
    if (
      newReceipt.admission_no &&
      studentInstallments.length > 0 &&
      newReceipt.installment_ids.length > 0
    ) {
      payload.fee_installments = newReceipt.installment_ids.map((id) => ({
        student_fee_installment_id: id,
        amount: Number(newReceipt.installment_amounts[id]) || 0,
      }));
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}create-receipt-v2`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: `Receipt added successfully! Receipt Number: ${response.data.receipt_number}`,
          variant: 'success',
        });
        setNewReceipt({
          from_account_id: '',
          from_account: '',
          to_account_id: '',
          to_account: '',
          amount: '',
          date: newReceipt.date,
          description: '',
          admission_no: '',
          academic_year_id: selectedAcademicYear?.id || '',
          installment_ids: [],
          installment_amounts: {},
        });
        setStudentInstallments([]);
        setSelectedStudent(null);
        // Focus to received from input after save
        setTimeout(() => {
          if (admissionInputRef.current) admissionInputRef.current.focus();
        }, 0);
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to add receipt',
          variant: 'danger',
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'An unexpected error occurred';
      setAlert({
        show: true,
        message: `Error: ${errorMessage}`,
        variant: 'danger',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFromAccountChange = (e) => {
    const input = e.target.value;
    setNewReceipt({ ...newReceipt, from_account: input, from_account_id: '' });
    setShowFromDropdown(true);
    setFromDropdownIndex(-1);
    if (input.trim() === '') {
      setFilteredFromAccounts([]);
    } else {
      const matches = accounts.filter((acc) =>
        acc.name.toLowerCase().includes(input.toLowerCase()),
      );
      setFilteredFromAccounts(matches);
    }
  };

  // Keyboard navigation for Received From dropdown
  const handleFromAccountKeyDown = (e) => {
    if (!showFromDropdown || filteredFromAccounts.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFromDropdownIndex((prev) => (prev + 1) % filteredFromAccounts.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFromDropdownIndex(
        (prev) =>
          (prev - 1 + filteredFromAccounts.length) %
          filteredFromAccounts.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (
        fromDropdownIndex >= 0 &&
        fromDropdownIndex < filteredFromAccounts.length
      ) {
        selectFromAccount(filteredFromAccounts[fromDropdownIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowFromDropdown(false);
    }
  };

  const selectFromAccount = (account) => {
    setNewReceipt({
      ...newReceipt,
      from_account_id: account.id,
      from_account: account.name,
    });
    setFilteredFromAccounts([]);
    setShowFromDropdown(false);
  };

  const handleToAccountChange = (e) => {
    const input = e.target.value;
    setNewReceipt({ ...newReceipt, to_account: input, to_account_id: '' });
    setShowToDropdown(true);
    setToDropdownIndex(-1);
    if (input.trim() === '') {
      setFilteredToAccounts([]);
    } else {
      const matches = cashAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(input.toLowerCase()),
      );
      setFilteredToAccounts(matches);
    }
  };

  // Keyboard navigation for To Account dropdown
  const handleToAccountKeyDown = (e) => {
    if (!showToDropdown || filteredToAccounts.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setToDropdownIndex((prev) => (prev + 1) % filteredToAccounts.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setToDropdownIndex(
        (prev) =>
          (prev - 1 + filteredToAccounts.length) % filteredToAccounts.length,
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (toDropdownIndex >= 0 && toDropdownIndex < filteredToAccounts.length) {
        selectToAccount(filteredToAccounts[toDropdownIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowToDropdown(false);
    }
  };

  const selectToAccount = (account) => {
    setNewReceipt({
      ...newReceipt,
      to_account_id: account.id,
      to_account: account.name,
    });
    setFilteredToAccounts([]);
    setShowToDropdown(false);
  };

  return (
    <Layout>
      <div
        style={{
          background: '#fff',
          paddingTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ maxWidth: '800px', width: '100%' }}>
          {/* Floating Alert (bottom left) */}
          {alert.show && (
            <div
              style={{
                position: 'fixed',
                left: '18px',
                bottom: '18px',
                zIndex: 9999,
                minWidth: '180px',
                maxWidth: '320px',
                pointerEvents: 'auto',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: '6px',
                  background:
                    alert.variant === 'success'
                      ? '#e6f7e6'
                      : alert.variant === 'danger'
                        ? '#ffeaea'
                        : '#fff8e1',
                  color: '#5c4200',
                  marginBottom: 0,
                  fontWeight: 500,
                  fontSize: '0.92rem',
                  padding: '6px 32px 6px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '320px',
                }}
              >
                <span style={{ flex: 1 }}>{alert.message}</span>
                <button
                  onClick={() =>
                    setAlert({ show: false, message: '', variant: '' })
                  }
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#5c4200',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    padding: '0 2px',
                    cursor: 'pointer',
                    borderRadius: '2px',
                    height: '22px',
                    width: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          {/* Form Card */}
          <div>
            {/* Compact Top Bar inside card */}
            <div
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px 12px 0 0',
                padding: '16px 24px',
                marginBottom: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <FaReceipt style={{ color: 'white', fontSize: '1.5rem' }} />
                <h3
                  style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '1.2rem',
                    fontWeight: '700',
                  }}
                >
                  Create Receipt
                </h3>
              </div>
              <Button
                variant="link"
                onClick={() => navigate('/receipt-list')}
                style={{
                  color: 'white',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  padding: 0,
                  marginLeft: 'auto',
                  minWidth: 0,
                  minHeight: 0,
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                title="Go to Receipt List"
              >
                <FaList />
              </Button>
            </div>
            {/* Add padding below top bar for body */}
            <div>
              <Form>
                {/* Receipt Number, Admission Number, Date Row */}
                <div
                  style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '24px',
                    marginTop: '24px',
                  }}
                >
                  {/* Receipt Number */}
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Receipt No.
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="New"
                      disabled
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        fontStyle: 'italic',
                      }}
                    />
                  </Form.Group>
                  {/* Admission Number */}
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Admission No.
                    </Form.Label>
                    <Form.Control
                      type="number"
                      ref={admissionInputRef}
                      value={newReceipt.admission_no}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value || value.toString().trim() === '') {
                          setSelectedStudent(null);
                          setFeeSummary(null);
                          setHelperAcademicYear(null);
                          setAcademicYearMismatch(false);
                          setStudentInstallments([]);
                          setNewReceipt((prev) => ({
                            ...prev,
                            admission_no: '',
                            installment_ids: [],
                            installment_amounts: {},
                            from_account_id: '',
                            from_account: '',
                            amount: '',
                          }));
                        } else {
                          setNewReceipt((prev) => ({
                            ...prev,
                            admission_no: value,
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value && newReceipt.academic_year_id) {
                          fetchStudentFeeReceiptHelper(
                            e.target.value,
                            newReceipt.academic_year_id,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === 'Tab') &&
                          newReceipt.admission_no &&
                          newReceipt.academic_year_id
                        ) {
                          fetchStudentFeeReceiptHelper(
                            newReceipt.admission_no,
                            newReceipt.academic_year_id,
                          );
                        }
                      }}
                      placeholder="Enter admission number"
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                      }}
                    />
                    {helperAcademicYear && (
                      <div
                        style={{
                          marginTop: '6px',
                          fontSize: '0.82rem',
                          color: academicYearMismatch ? '#c52828' : '#155724',
                          fontWeight: 500,
                        }}
                      >
                        Installments belong to: {helperAcademicYear.name}
                        {selectedAcademicYear?.name
                          ? ` (current selected ${selectedAcademicYear.name})`
                          : ''}
                        {academicYearMismatch
                          ? ' - PLEASE SWITCH ACADEMIC YEAR'
                          : ''}
                      </div>
                    )}
                  </Form.Group>
                  {/* Date */}
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Date <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={newReceipt.date}
                      onChange={(e) =>
                        setNewReceipt({ ...newReceipt, date: e.target.value })
                      }
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                      }}
                      required
                      ref={dateInputRef}
                    />
                  </Form.Group>
                </div>

                {/* Installments Box */}
                {selectedStudent && (
                  <div
                    style={{
                      marginBottom: '24px',
                      padding: '16px',
                      backgroundColor: '#f8f9ff',
                      borderRadius: '8px',
                      border: '1px solid #e3e7ff',
                    }}
                  >
                    <h6
                      style={{
                        margin: '0 0 8px 0',
                        color: '#2c3e50',
                        fontSize: '1rem',
                        fontWeight: '600',
                      }}
                    >
                      Installments
                    </h6>
                    {academicYearMismatch && (
                      <div
                        style={{
                          marginBottom: '8px',
                          fontSize: '0.85rem',
                          color: '#c52828',
                          background: '#fff4f2',
                          border: '1px solid #f5c6cb',
                          borderRadius: '6px',
                          padding: '8px',
                        }}
                      >
                        Academic year mismatch: installment data belongs to{' '}
                        {helperAcademicYear?.name || 'another year'} but the
                        selected academic year is{' '}
                        {selectedAcademicYear?.name || 'not selected'}.
                        Installments are shown read-only and receipt cannot be
                        saved until the academic year matches.
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {studentInstallments.length === 0 ? (
                        <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                          No due installments found.
                        </div>
                      ) : (
                        studentInstallments.map((inst) => (
                          <div
                            key={inst.student_fee_installment_id}
                            style={{
                              display: 'grid',
                              alignItems: 'center',
                              gridTemplateColumns:
                                '24px minmax(140px, 1.2fr) minmax(100px, 1fr) minmax(80px, 1fr) 100px 140px',
                              gap: '10px',
                              background: inst.is_selectable_for_receipt
                                ? '#f0fff4'
                                : '#f8f9fa',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                            }}
                          >
                            <input
                              type="checkbox"
                              disabled={
                                academicYearMismatch ||
                                !inst.is_selectable_for_receipt
                              }
                              checked={newReceipt.installment_ids.includes(
                                inst.student_fee_installment_id,
                              )}
                              onChange={(e) => {
                                if (academicYearMismatch) return;
                                const checked = e.target.checked;
                                setNewReceipt((prev) => {
                                  let ids = [...prev.installment_ids];
                                  let amounts = { ...prev.installment_amounts };
                                  if (checked) {
                                    ids.push(inst.student_fee_installment_id);
                                    amounts[inst.student_fee_installment_id] =
                                      inst.balance_amount;
                                  } else {
                                    ids = ids.filter(
                                      (id) =>
                                        id !== inst.student_fee_installment_id,
                                    );
                                    delete amounts[
                                      inst.student_fee_installment_id
                                    ];
                                  }
                                  const total = Object.values(amounts).reduce(
                                    (sum, val) => sum + Number(val),
                                    0,
                                  );
                                  return {
                                    ...prev,
                                    installment_ids: ids,
                                    installment_amounts: amounts,
                                    amount: total.toString(),
                                  };
                                });
                              }}
                            />
                            <span
                              style={{
                                fontWeight: '600',
                                color: '#2c3e50',
                                fontSize: '0.9rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {inst.title}
                            </span>
                            <span
                              style={{
                                color: '#495057',
                                fontSize: '0.85rem',
                              }}
                            >
                              ₹{inst.balance_amount}
                            </span>
                            <span
                              style={{
                                color:
                                  inst.status === 'paid'
                                    ? '#28a745'
                                    : '#dc3545',
                                fontWeight: '500',
                                fontSize: '0.85rem',
                              }}
                            >
                              {inst.status}
                            </span>
                            <input
                              type="number"
                              min="0"
                              max={inst.balance_amount}
                              disabled={
                                academicYearMismatch ||
                                !inst.is_selectable_for_receipt ||
                                !newReceipt.installment_ids.includes(
                                  inst.student_fee_installment_id,
                                )
                              }
                              value={
                                newReceipt.installment_amounts[
                                  inst.student_fee_installment_id
                                ] || ''
                              }
                              onChange={(e) => {
                                if (academicYearMismatch) return;
                                const val = e.target.value;
                                setNewReceipt((prev) => {
                                  let amounts = { ...prev.installment_amounts };
                                  amounts[inst.student_fee_installment_id] =
                                    val;
                                  const total = Object.values(amounts).reduce(
                                    (sum, v) => sum + Number(v),
                                    0,
                                  );
                                  return {
                                    ...prev,
                                    installment_amounts: amounts,
                                    amount: total.toString(),
                                  };
                                });
                              }}
                              style={{
                                width: '100%',
                                borderRadius: '4px',
                                border: '1px solid #dee2e6',
                                padding: '4px 8px',
                                fontSize: '0.9rem',
                              }}
                              placeholder="Amount"
                            />
                            <span
                              style={{
                                color: '#667eea',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {inst.fee_structure_name}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* From and To Accounts Row */}
                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
                >
                  {/* From Account Field */}
                  <Form.Group style={{ flex: '1' }} ref={fromDropdownRef}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Received From <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={newReceipt.from_account}
                        onChange={handleFromAccountChange}
                        onKeyDown={handleFromAccountKeyDown}
                        onFocus={() => setShowFromDropdown(true)}
                        placeholder="Search account..."
                        style={{
                          borderRadius: '6px',
                          padding: '10px 12px',
                          border: newReceipt.from_account_id
                            ? '1px solid #28a745'
                            : '1px solid #dee2e6',
                          fontSize: '0.9rem',
                          backgroundColor: newReceipt.from_account_id
                            ? '#f0fff4'
                            : 'white',
                        }}
                        required
                        ref={fromInputRef}
                        disabled={
                          !!newReceipt.admission_no && !!selectedStudent
                        }
                      />
                      {newReceipt.from_account_id && (
                        <FaCheck
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#28a745',
                          }}
                        />
                      )}
                      {showFromDropdown && filteredFromAccounts.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                          }}
                        >
                          {filteredFromAccounts.map((acc, idx) => (
                            <div
                              key={acc.id}
                              onClick={() => selectFromAccount(acc)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '0.9rem',
                                transition: 'background 0.2s',
                                background:
                                  fromDropdownIndex === idx
                                    ? '#e9ecef'
                                    : 'white',
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = '#f8f9fa')
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background =
                                  fromDropdownIndex === idx
                                    ? '#e9ecef'
                                    : 'white')
                              }
                            >
                              {acc.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                  {/* To Account Field */}
                  <Form.Group style={{ flex: '1' }} ref={toDropdownRef}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      To Account (Cash/Bank){' '}
                      <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={newReceipt.to_account}
                        onChange={handleToAccountChange}
                        onKeyDown={handleToAccountKeyDown}
                        onFocus={() => setShowToDropdown(true)}
                        placeholder="Search cash/bank..."
                        style={{
                          borderRadius: '6px',
                          padding: '10px 12px',
                          border: newReceipt.to_account_id
                            ? '1px solid #28a745'
                            : '1px solid #dee2e6',
                          fontSize: '0.9rem',
                          backgroundColor: newReceipt.to_account_id
                            ? '#f0fff4'
                            : 'white',
                        }}
                        required
                      />
                      {newReceipt.to_account_id && (
                        <FaCheck
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#28a745',
                          }}
                        />
                      )}
                      {showToDropdown && filteredToAccounts.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            marginTop: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                          }}
                        >
                          {filteredToAccounts.map((acc, idx) => (
                            <div
                              key={acc.id}
                              onClick={() => selectToAccount(acc)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '0.9rem',
                                transition: 'background 0.2s',
                                background:
                                  toDropdownIndex === idx ? '#e9ecef' : 'white',
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = '#f8f9fa')
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background =
                                  toDropdownIndex === idx ? '#e9ecef' : 'white')
                              }
                            >
                              {acc.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </div>

                {/* Amount and Description Row */}
                <div
                  style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
                >
                  {/* Amount Field */}
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '1rem',
                      }}
                    >
                      Amount <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <div style={{ position: 'relative' }}>
                      <span
                        style={{
                          position: 'absolute',
                          left: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#667eea',
                          fontWeight: '700',
                          fontSize: '1.2rem',
                        }}
                      >
                        ₹
                      </span>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={newReceipt.amount}
                        onChange={(e) => {
                          // If fee receipt, amount is auto-calculated from installments
                          if (
                            newReceipt.admission_no &&
                            studentInstallments.length > 0
                          )
                            return;
                          setNewReceipt({
                            ...newReceipt,
                            amount: e.target.value,
                          });
                        }}
                        onWheel={(e) => e.target.blur()}
                        placeholder="0.00"
                        style={{
                          borderRadius: '8px',
                          padding: '14px 16px 14px 40px',
                          border: '2px solid #667eea',
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          backgroundColor: '#f8f9ff',
                          color: '#2c3e50',
                        }}
                        required
                        disabled={
                          !!newReceipt.admission_no &&
                          studentInstallments.length > 0
                        }
                      />
                    </div>
                  </Form.Group>
                  {/* Description Field */}
                  <Form.Group style={{ flex: '1' }}>
                    <Form.Label
                      style={{
                        fontWeight: '600',
                        color: '#2c3e50',
                        fontSize: '0.9rem',
                      }}
                    >
                      Description
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={newReceipt.description}
                      onChange={(e) =>
                        setNewReceipt({
                          ...newReceipt,
                          description: e.target.value,
                        })
                      }
                      onFocus={(e) => (e.target.style.height = '72px')}
                      onBlur={(e) => {
                        if (!e.target.value) e.target.style.height = '55px';
                      }}
                      onInput={(e) => {
                        if (e.target.value) e.target.style.height = '72px';
                        else e.target.style.height = '55px';
                      }}
                      placeholder="Add any notes or description..."
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        resize: 'none',
                        height: newReceipt.description ? '72px' : '55px',
                        transition: 'height 0.2s',
                      }}
                    />
                  </Form.Group>
                </div>

                {/* Submit Button */}
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="save-tooltip">Save (Ctrl+S)</Tooltip>}
                  >
                    <Button
                      type="submit"
                      variant="primary"
                      onClick={addReceipt}
                      disabled={loading}
                      style={{
                        background:
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 24px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '140px',
                        justifyContent: 'center',
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FaSave /> Save
                        </>
                      )}
                    </Button>
                  </OverlayTrigger>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Receipt;
